import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Gift, Loader2 } from 'lucide-react';
import { CONTRACT_PARAMS } from '../ton/config';
import { useGiftTickets } from '../hooks/useGiftTickets';
import { WalletErrorNotice } from './WalletErrorNotice';
import { formatTon } from '../lib/format';
import { ticketNoun } from '../i18n/languages';

interface GiftTicketModalProps {
  ticketPrice: number;
  onClose: () => void;
}

// Keep only digits and a single decimal separator; accept both '.' and ','.
function sanitizeQty(raw: string): string {
  const cleaned = raw.replace(/[^0-9.,]/g, '');
  const sepIndex = cleaned.search(/[.,]/);
  if (sepIndex === -1) return cleaned;
  const head = cleaned.slice(0, sepIndex + 1);
  const tail = cleaned.slice(sepIndex + 1).replace(/[.,]/g, '');
  return head + tail;
}

const parseQty = (raw: string): number => parseFloat(raw.replace(',', '.'));

/**
 * Modal for gifting lottery tickets to the owner of a numeric invite key.
 * The recipient's wallet gets the tickets and the PRMD mint; commissions (and
 * the gifter's registration with their incoming refKey, if new) follow the
 * gifter's own referral chain.
 */
export function GiftTicketModal({ ticketPrice, onClose }: GiftTicketModalProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { giftTickets, pending, error, lastTxAt } = useGiftTickets();
  const [keyInput, setKeyInput] = useState('');
  const [qtyInput, setQtyInput] = useState('1');

  const minQty = CONTRACT_PARAMS.minEntryTon / ticketPrice;
  const qty = parseQty(qtyInput);
  const totalTon = Number.isFinite(qty) ? Math.round(qty * ticketPrice * 1e9) / 1e9 : NaN;
  const qtyValid = Number.isFinite(qty) && totalTon >= CONTRACT_PARAMS.minEntryTon - 1e-9;
  const keyValid = /^[0-9]+$/.test(keyInput.trim());
  const sent = lastTxAt !== null;

  const stepQty = (delta: number) => {
    const base = Number.isFinite(qty) ? qty : minQty;
    const next = Math.max(minQty, Math.round((base + delta) * 100) / 100);
    setQtyInput(String(next).replace('.', ','));
  };

  return (
    <div
      className="sand-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget && !pending) onClose();
      }}
    >
      <div className="sand-sheet">
        <div className="sand-sheet__top">
          <Gift size={22} aria-hidden="true" />
          <h3>{t('lottery.giftTitle')}</h3>
          <button type="button" className="sand-sheet__close" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="sand-sheet__info">{t('lottery.giftSubtitle')}</div>

        <label className="ticket-qty ticket-qty--gift">
          <span className="ticket-qty__label">{t('lottery.giftKeyLabel')}</span>
          <input
            className="ticket-qty__input ticket-qty__input--key"
            type="text"
            inputMode="numeric"
            value={keyInput}
            onChange={(e) => setKeyInput(e.target.value.replace(/[^0-9]/g, ''))}
            disabled={pending}
            aria-label={t('lottery.giftKeyLabel')}
          />
        </label>
        <p className="hint hint--field">{t('lottery.giftKeyExample')}</p>

        <div className="ticket-qty ticket-qty--gift">
          <span className="ticket-qty__label">{t('lottery.ticketCount')}</span>
          <div className="ticket-qty__control">
            <button
              type="button"
              className="ticket-qty__step"
              onClick={() => stepQty(-1)}
              disabled={pending || (Number.isFinite(qty) && qty <= minQty)}
              aria-label={t('lottery.decrease')}
            >
              −
            </button>
            <input
              className="ticket-qty__input"
              type="text"
              inputMode="decimal"
              value={qtyInput}
              onChange={(e) => setQtyInput(sanitizeQty(e.target.value))}
              disabled={pending}
              aria-label={t('lottery.ticketCount')}
            />
            <button
              type="button"
              className="ticket-qty__step"
              onClick={() => stepQty(1)}
              disabled={pending}
              aria-label={t('lottery.increase')}
            >
              +
            </button>
          </div>
        </div>

        <button
          type="button"
          className="buy-ticket-btn"
          disabled={pending || !keyValid || !qtyValid}
          onClick={() => {
            void giftTickets(Number(keyInput.trim()), totalTon);
          }}
        >
          {pending ? (
            <>
              <Loader2 className="spin" size={18} aria-hidden="true" /> {t('lottery.sending')}
            </>
          ) : !keyValid ? (
            t('lottery.giftEnterKey')
          ) : !qtyValid ? (
            t('lottery.buyMin', { amount: formatTon(CONTRACT_PARAMS.minEntryTon) })
          ) : (
            t('lottery.giftSend', {
              qty: formatTon(qty),
              word: ticketNoun(lang, qty),
              total: formatTon(totalTon),
              key: keyInput.trim(),
            })
          )}
        </button>

        <p className="hint">{t('lottery.giftHint')}</p>
        {error && <WalletErrorNotice message={error} />}
        {pending && (
          <div className="banner banner--active">
            <Loader2 className="spin" size={18} aria-hidden="true" />{' '}
            {t('lottery.confirmInWallet')}
          </div>
        )}
        {!pending && sent && (
          <div className="banner banner--success">
            {t('lottery.giftSent', { key: keyInput.trim() })}
          </div>
        )}
      </div>
    </div>
  );
}
